import * as React from "react";

import { splitTopicQuery } from "@/shared/domain/skills/topicReference";
import { useOrgTopicNames } from "@/shared/domain/topics/hooks";
import {
  fieldArity,
  hasConflict,
  sourceDataType,
  type FieldArity,
  type UrdfBindableMessagePath,
} from "@/shared/domain/urdf";
import { useUrdfBindableMessagePaths } from "@/shared/domain/urdf/hooks";

import { splitElementIndex } from "./jointBindingDraft";

export interface TopicPathParts {
  /** The topic-name half of the text. Exact — matching never globs or prefixes. */
  topic: string;
  /**
   * The message-path half **as typed**, `[n]` included: `position[3]`.
   *
   * What the user sees. {@link messageField} and {@link element} are the two
   * halves of it a declaration is actually written from.
   */
  messagePath: string;
  /** The field alone, with any trailing index removed — what the catalogue lists. */
  messageField: string;
  /** The element index written into the path, if it carried one. */
  element?: number;
  /** True once a *known* topic is followed by a `.`: the menu is offering fields. */
  fieldMode: boolean;
  /** Every topic name in the org, for the split and for the menu. */
  topicNames: readonly string[];
  /** The active topic's fields, empty until one is settled on. */
  fields: readonly UrdfBindableMessagePath[];
  /** The catalogue's description of exactly `messagePath`, when it has one. */
  field?: UrdfBindableMessagePath;
  /**
   * The source schema's spelling of `field`, when it is one — see
   * {@link sourceDataType}. `undefined` where the catalogue reports none, and
   * equally where what it reports only restates the canonical type and so says
   * nothing a reader can put on screen.
   */
  dataType?: string;
  /**
   * Whether `field` holds many values, one, or whether the catalogue cannot say
   * — and `undefined` while it has not yet been asked.
   *
   * The fourth state is not pedantry. A row loaded with a scalar binding renders
   * before the field fetch resolves, and for that moment no field is found;
   * calling that "unknown" published a definite answer to a question nobody had
   * asked yet, and every scalar-bound row flashed "the catalogue does not report
   * this field's type" before quietly retracting it. An unknown state is
   * rendered as an unknown state, the same way the wizard's own `phase` is.
   */
  arity: FieldArity | undefined;
  /**
   * The topic's partitions disagree about this field's type.
   *
   * Distinct from "no type reported": the catalogue knows, and what it knows is
   * that two schema versions of this topic spell the field differently. Worth
   * saying in those words, because the fix is different — the user picks which
   * recording they mean, rather than waiting for a catalogue to catch up.
   */
  typeConflict: boolean;
  isLoading: boolean;
}

/**
 * Splits a `topic.message.path` string into its two halves, and describes the
 * field it lands on.
 *
 * The split is **catalogue-aware, never positional**. Topic names contain dots
 * in the wild (`/proto/executive.extended_status/context`), so the first dot is
 * not the boundary; the longest known topic name that prefixes the text at a dot
 * boundary is. That is `splitTopicQuery`, shared verbatim with the skill body
 * editor's two-stage autocomplete rather than reimplemented — one boundary rule,
 * one place.
 *
 * Both queries are TanStack-cached by `(org, topic)`, so every joint row calling
 * this shares one topic-name fetch and one fetch per distinct topic, however
 * many rows a robot has.
 *
 * @param text what the user has typed, or the joined form of an existing binding
 * @param orgId the org whose catalogue to search
 */
export function useTopicPathParts(
  text: string,
  orgId?: string,
): TopicPathParts {
  const topicKeys = useOrgTopicNames(orgId);
  const topicNames = React.useMemo(
    () => topicKeys.data ?? [],
    [topicKeys.data],
  );

  const { topicPart, fieldFragment } = React.useMemo(
    () => splitTopicQuery(text, topicNames),
    [text, topicNames],
  );

  // The field fetch is per *known* topic: asking for the fields of a topic the
  // org does not have would be a request per keystroke for nothing.
  const activeTopic =
    fieldFragment !== null && topicNames.includes(topicPart) ? topicPart : null;
  const topicFields = useUrdfBindableMessagePaths(activeTopic, orgId);
  const fields = React.useMemo(
    () => topicFields.data ?? [],
    [topicFields.data],
  );

  const messagePath = fieldFragment ?? "";
  // The catalogue lists `position`, never `position[3]`, so the index comes off
  // before the lookup — otherwise typing an index would lose the field's type
  // and with it the guard that made the index necessary.
  const { path: messageField, element } = splitElementIndex(messagePath);
  const field = fields.find((candidate) => candidate.path === messageField);
  const spelling = field === undefined ? undefined : sourceDataType(field);

  // Both queries feed the answer: until the org's topic names are in, the text
  // cannot even be split into a topic and a field, so *every* path looks like a
  // field the catalogue has never heard of.
  const isLoading = topicKeys.isLoading || topicFields.isLoading;

  return {
    topic: topicPart,
    messagePath,
    messageField,
    ...(element !== undefined ? { element } : {}),
    fieldMode: fieldFragment !== null,
    topicNames,
    fields,
    ...(field !== undefined ? { field } : {}),
    ...(spelling !== undefined ? { dataType: spelling } : {}),
    // A field the catalogue does not describe — because it is not in the
    // catalogue, because this API predates the widening, or because the topic's
    // partitions disagree — is "unknown", never "scalar". The difference is the
    // whole array trap. And while a query is still in flight the catalogue has
    // not described anything yet, which is not the same as describing nothing.
    arity: arityOf(field, isLoading),
    typeConflict:
      field !== undefined && hasConflict(field, "canonical_data_type"),
    isLoading,
  };
}

/**
 * A field's arity, or `undefined` while the catalogue is still being read.
 *
 * A found field answers even mid-fetch — the description in hand is the
 * description, and a background refetch does not un-know it. It is only the
 * *absence* of one that has to wait: mid-fetch, "no such field" and "not looked
 * yet" are indistinguishable, and only the second is a reason to say nothing.
 */
function arityOf(
  field: UrdfBindableMessagePath | undefined,
  isLoading: boolean,
): FieldArity | undefined {
  if (field !== undefined) {
    return fieldArity(field);
  }
  return isLoading ? undefined : "unknown";
}
